/* eslint-disable react-refresh/only-export-components */
import { useMemo, useRef } from 'react';
import { HStack, IconButton, Tooltip } from '@chakra-ui/react';
import { AddIcon } from '@chakra-ui/icons';
import { Button } from '@/components/v4';
import { CustomSelect, Option } from 'components/v4/CustomSelect';
import { BsThreeDotsVertical } from 'react-icons/bs';
import AGGridMain from '@/components/tables/AGGrid/AGGridMain';
import { serviceTypeRender } from './constants';
import config from '@/config.json';

export const makeServiceNameColumn = () => ({
    headerName: 'Service Name',
    field: 'name',
    flex: 1,
});

export const makeServiceTypeColumn = () => ({
    headerName: 'Service Type',
    field: 'serviceType',
    flex: 1,
    cellRenderer: (params) => serviceTypeRender(params.data) || null,
});

export const makeVisibleScopeColumn = () => ({
    headerName: 'Visible Scope',
    field: 'visibleScope',
    flex: 1,
    valueFormatter: (params) => config.scope.json[params.value] || params.value,
    filter: 'agSetColumnFilter',
    filterParams: {
        valueFormatter: (params) =>
            config.scope.json[params.value] || params.value,
        cellRenderer: (params) =>
            config.scope.json[params.value] || params.value,
    },
    cellRenderer: (params) => {
        const value = config.scope.json[params.value];
        return value ? <span>{value}</span> : null;
    },
});

export const makeActionColumn = (darkMode, onEdit) => ({
    headerName: 'Action',
    field: 'action',
    maxWidth: 100,
    lockPosition: 'right',
    filter: false,
    suppressMovable: true,
    lockVisible: true,
    suppressColumnsToolPanel: true,
    suppressHeaderMenuButton: true,
    suppressHeaderFilterButton: true,
    enableRowGroup: false,
    resizable: false,
    sortable: false,
    cellRenderer: (params) => (
        <Tooltip label="Edit Service Profile">
            <IconButton
                icon={
                    <BsThreeDotsVertical fill={darkMode ? 'white' : 'black'} />
                }
                aria-label="Edit"
                variant="ghost"
                onClick={() => onEdit(params.data)}
                _hover={{
                    bg: darkMode ? 'whiteAlpha.200' : 'blackAlpha.100',
                }}
                _active={{
                    bg: darkMode ? 'whiteAlpha.300' : 'blackAlpha.200',
                }}
                _focus={{ boxShadow: 'none' }}
            />
        </Tooltip>
    ),
});

export function ServiceProfileTableExtras({
    onAdd,
    addTooltipLabel = 'Add Service Profile',
    level,
    levelDropdown,
    onLevelChange,
    addButtonText = 'Service Profile',
}) {
    return (
        <HStack pl={5} spacing={2}>
            <Tooltip
                label={addTooltipLabel}
                placement="bottom"
                hasArrow
                portalProps={{ containerRef: undefined }}>
                <div style={{ position: 'relative', display: 'inline-block' }}>
                    <Button size="sm" leftIcon={<AddIcon />} onClick={onAdd}>
                        {addButtonText}
                    </Button>
                </div>
            </Tooltip>
            <HStack sx={{ '& select': { borderRadius: '6px' } }}>
                <span className="text-sm font-medium">Filter:</span>
                <CustomSelect
                    value={level}
                    onChange={onLevelChange}
                    width="150px"
                    size="sm"
                    h="32px">
                    {levelDropdown.map(({ label, value }) => (
                        <Option key={label} value={value}>
                            {label}
                        </Option>
                    ))}
                </CustomSelect>
            </HStack>
        </HStack>
    );
}

export function ServiceProfileTable({
    staticColumns,
    titleName,
    addTooltipLabel,
    addButtonText,
    data,
    isLoading,
    isFetching,
    darkMode,
    onEdit,
    onAdd,
    refetch,
    level,
    levelDropdown,
    onLevelChange,
}) {
    const tableRef = useRef();

    const columns = useMemo(
        () => [...staticColumns, makeActionColumn(darkMode, onEdit)],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [darkMode],
    );

    const tableData = useMemo(() => ({ columns, data }), [columns, data]);

    return (
        <AGGridMain
            tableData={tableData}
            titleName={titleName}
            refetch={refetch}
            extraComponents={
                <ServiceProfileTableExtras
                    onAdd={onAdd}
                    addTooltipLabel={addTooltipLabel}
                    level={level}
                    levelDropdown={levelDropdown}
                    onLevelChange={onLevelChange}
                    addButtonText={addButtonText}
                />
            }
            isLoading={isLoading}
            isFetching={isFetching}
            gridRef={tableRef}
        />
    );
}
